import { PDFDocument, rgb } from "pdf-lib";
import { PdfFontSet, replacedCharsMessage, type FallbackLoader, type RasterEnv } from "./pdfFonts";
import { stampOutputMetadata } from "./pdfMetadata";

export type PageSizeName = "A4" | "Letter";
export const PAGE_SIZES: Record<PageSizeName, [number, number]> = { A4: [595.28, 841.89], Letter: [612, 792] };

export interface TextPdfOptions {
  pageSize?: PageSizeName;
  fontSize?: number;
  title?: string;
  fallbackLoader?: FallbackLoader;
  /** Draws text no font can (default: the browser canvas; null or a test double to override). */
  rasterEnv?: RasterEnv | null;
  isCurrent?: () => boolean;
}

export interface TextPdfResult {
  bytes: Uint8Array;
  pages: number;
  warnings: string[];
}

/** Decodes a text file: UTF-8 (BOM removed) or UTF-16 by its byte-order mark; other files as UTF-8. */
export function decodeTextFile(buf: ArrayBuffer): string {
  const u = new Uint8Array(buf);
  if (u[0] === 0xff && u[1] === 0xfe) return new TextDecoder("utf-16le").decode(u.subarray(2));
  if (u[0] === 0xfe && u[1] === 0xff) return new TextDecoder("utf-16be").decode(u.subarray(2));
  return new TextDecoder("utf-8").decode(u).replace(/^﻿/, "");
}

/** Plain text as a paginated PDF: any line-ending style, tabs, form feeds, long words, non-Latin where possible. */
export async function convertTextToPdfDocument(text: string, opts: TextPdfOptions = {}): Promise<TextPdfResult> {
  const [pw, ph] = PAGE_SIZES[opts.pageSize ?? "A4"];
  const size = opts.fontSize ?? 11;
  const margin = 56;
  const lineH = size * 1.4;
  const pdf = await PDFDocument.create();
  const fonts = new PdfFontSet(pdf, opts.fallbackLoader, opts.rasterEnv);
  await fonts.prepare(text);
  fonts.scan(text);
  const maxW = pw - margin * 2;
  let page = pdf.addPage([pw, ph]);
  let y = ph - margin;
  const newPage = () => { page = pdf.addPage([pw, ph]); y = ph - margin; };
  const family = "sans" as const;

  const drawLine = (line: string) => {
    if (y < margin + size) newPage();
    let x = margin;
    for (const r of fonts.runs(line, family)) {
      fonts.drawRun(page, r, { x, y: y - size, size, color: rgb(0, 0, 0) });
      x += r.font.widthOfTextAtSize(r.text, size);
    }
    y -= lineH;
  };

  const w = (s: string) => fonts.width(s, size, family);
  const lines = text.split(/\r\n|\r|\n/);
  for (let li = 0; li < lines.length; li++) {
    if (opts.isCurrent && !opts.isCurrent()) throw new Error("cancelled");
    // A form feed starts a new page.
    const pieces = lines[li].split("\f");
    pieces.forEach((piece, pi) => {
      if (pi > 0) newPage();
      const expanded = piece.replace(/\t/g, "    ");
      // Leading spaces are kept (indented code, lists).
      const lead = expanded.match(/^ */)![0].length;
      const body = expanded.slice(lead);
      const indent = lead * w(" ");
      if (!body.trim()) { drawLine(""); return; }
      let cur = " ".repeat(lead);
      let curW = indent;
      for (const word of body.split(" ")) {
        const ww = w(word);
        const sep = cur.trim() ? " " : "";
        if (cur.trim() && curW + w(sep) + ww > maxW) {
          drawLine(cur);
          cur = " ".repeat(lead);
          curW = indent;
        }
        if (ww > maxW - indent) {
          // A word wider than the line (a long URL): break it by characters.
          let chunk = "";
          for (const ch of word) {
            if (w(cur + chunk + ch) > maxW && (chunk || cur.trim())) {
              drawLine(cur + chunk);
              cur = " ".repeat(lead);
              chunk = "";
            }
            chunk += ch;
          }
          cur += chunk;
          curW = w(cur);
        } else {
          cur += (cur.trim() ? " " : "") + word;
          curW = w(cur);
        }
      }
      drawLine(cur);
    });
  }
  stampOutputMetadata(pdf, { title: opts.title, fallbackTitle: "Text document" });
  const warnings: string[] = [];
  const m = replacedCharsMessage(fonts);
  if (m) warnings.push(m);
  await fonts.flushRasters();
  return { bytes: await pdf.save(), pages: pdf.getPageCount(), warnings };
}
